import React, { useEffect, useState } from 'react';
import api from '../services/api';
import './FechamentoPorEmitente.css';

/**
 * O FECHAMENTO FINANCEIRO POR CNPJ — detalhe, faturamento e liquidação.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ESTE COMPONENTE NÃO CALCULA NADA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Todo número vem de `/ordens-servico/:id/fechamento`. Um segundo motor
 * tributário aqui dentro daria dois valores com o mesmo nome — e o da tela
 * seria o que a pessoa acredita, porque é o que ela vê.
 *
 * O que a tela decide é o que MOSTRAR e como: a ordem dos descontos, o que é
 * somável, e o que aparece quando falta dado.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CAMPO AUSENTE NÃO VIRA R$ 0,00
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * "Não há retenção" e "não sei se há" são respostas diferentes. A segunda
 * aparece como "não apurada", com o motivo ao lado — exibi-la como zero é
 * assim que se paga a mais e se descobre no fechamento do mês.
 */

const moeda = (v) =>
  v == null
    ? null
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

/** Valor monetário, ou a marca de "não apurado" — nunca um zero de enfeite. */
function Valor({ v, negativo }) {
  if (v == null) return <span className="fpe-ausente">não apurado</span>;
  return (
    <span className={negativo && v > 0 ? 'fpe-negativo' : undefined}>
      {negativo && v > 0 ? `− ${moeda(v)}` : moeda(v)}
    </span>
  );
}

const PAPEL = { pecas: 'Peças', servicos: 'Serviços' };

const ORIGEM = {
  registrado: { texto: 'Retenção registrada na fatura', classe: 'fpe-selo-registrado' },
  estimado: { texto: 'Estimativa pela configuração de hoje', classe: 'fpe-selo-estimado' },
  estimado_fatura_antiga: {
    texto: 'Estimativa — esta fatura é anterior ao registro por parcela',
    classe: 'fpe-selo-estimado',
  },
};

export default function FechamentoPorEmitente({ ordemServicoId, titulo }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [abertas, setAbertas] = useState({});

  useEffect(() => {
    let vivo = true;
    if (!ordemServicoId) return undefined;

    api
      .get(`/ordens-servico/${ordemServicoId}/fechamento`)
      .then((r) => vivo && setDados(r.data))
      .catch((e) =>
        vivo && setErro(e?.response?.data?.message || 'Não foi possível carregar o fechamento.')
      );

    return () => {
      vivo = false;
    };
  }, [ordemServicoId]);

  if (erro) return <div className="fpe-caixa fpe-erro">{erro}</div>;
  if (!dados) return null;

  const { conjunto, parcelas, totais, pendencias } = dados;
  const alternar = (id) => setAbertas((a) => ({ ...a, [id]: !a[id] }));

  return (
    <div className="fpe-caixa">
      <div className="fpe-topo">
        <h3>{titulo || (conjunto.ehConjunto ? 'Proposta conjunta — fechamento por CNPJ' : 'Fechamento financeiro')}</h3>
        {conjunto.ehConjunto && (
          <span className="fpe-chip">
            {parcelas.length} emitentes
          </span>
        )}
      </div>

      {conjunto.ehConjunto && conjunto.parcelasEsperadas > parcelas.length && (
        <p className="fpe-alerta">
          Este conjunto deveria ter {conjunto.parcelasEsperadas} parcelas e só {parcelas.length}{' '}
          {parcelas.length === 1 ? 'chegou' : 'chegaram'}. Confira antes de faturar.
        </p>
      )}

      {pendencias?.length > 0 && (
        <div className="fpe-pendencias">
          <strong>Falta informação para fechar:</strong>
          <ul>
            {pendencias.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {parcelas.map((p) => {
        const selo = ORIGEM[p.origemDosValores] || ORIGEM.estimado;
        const aberta = abertas[p.osId];

        return (
          <div className="fpe-parcela" key={p.osId}>
            <div className="fpe-emitente">
              <div>
                <strong>{p.emitente?.razaoSocial || p.emitente?.nomeFantasia || 'Emitente não identificado'}</strong>
                <div className="fpe-cnpj">
                  {p.emitente?.cnpj || 'sem CNPJ no cadastro'}
                  {p.papel && <> · {PAPEL[p.papel] || p.papel}</>}
                </div>
              </div>
              <span className={`fpe-selo ${selo.classe}`}>{selo.texto}</span>
            </div>

            <div className="fpe-linhas">
              <div><span>Bruto</span><Valor v={p.bruto} /></div>
              <div><span>Descontos</span><Valor v={p.desconto} negativo /></div>
              <div>
                <span>
                  Retenções
                  {p.memoria?.length > 0 && (
                    <button type="button" className="fpe-link" onClick={() => alternar(p.osId)}>
                      {aberta ? 'ocultar memória' : 'ver memória'}
                    </button>
                  )}
                </span>
                <Valor v={p.retencao} negativo />
              </div>
              <div>
                <span>
                  Taxa da plataforma
                  {p.taxaPercentual != null && <em> ({p.taxaPercentual}%)</em>}
                </span>
                <Valor v={p.taxa} negativo />
              </div>
              <div className="fpe-liquido">
                <span>Líquido</span>
                <Valor v={p.liquido} />
              </div>
            </div>

            {p.motivo && <p className="fpe-motivo">{p.motivo}</p>}

            {aberta && p.memoria?.length > 0 && (
              <table className="fpe-memoria">
                <thead>
                  <tr>
                    <th>Grupo</th>
                    <th>Incide sobre</th>
                    <th>Tributos</th>
                    <th>Alíquota</th>
                    <th>Base</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {p.memoria.map((l, i) => (
                    <tr key={`${p.osId}-${i}`}>
                      <td>{l.grupoRotulo}</td>
                      <td>{l.incidenciaRotulo}</td>
                      <td>
                        {l.componentes?.length
                          ? l.componentes.map((c) => `${c.tributoRotulo} ${c.aliquota}%`).join(' + ')
                          : l.tributoRotulo}
                      </td>
                      <td>{l.aliquota}%</td>
                      <td>{moeda(l.base)}</td>
                      <td>{moeda(l.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="fpe-rodape">
              <div>
                <span className="fpe-rotulo">Documentos</span>
                {p.documentos.notaFiscalPeca || p.documentos.notaFiscalServico ? (
                  <>
                    {p.documentos.notaFiscalPeca && <div>NF peças: {p.documentos.notaFiscalPeca}</div>}
                    {p.documentos.notaFiscalServico && <div>NF serviços: {p.documentos.notaFiscalServico}</div>}
                  </>
                ) : (
                  <div className="fpe-ausente">nenhuma nota anexada</div>
                )}
              </div>

              <div>
                <span className="fpe-rotulo">Fatura</span>
                {p.fatura ? (
                  <div>
                    {p.fatura.numero} · {p.fatura.status}
                  </div>
                ) : (
                  <div className="fpe-ausente">ainda não faturada</div>
                )}
              </div>

              <div>
                <span className="fpe-rotulo">Situação</span>
                <div>{p.situacao}</div>
                <div>
                  Pago: <Valor v={p.valorPago} /> · Pendente: <Valor v={p.saldoPendente} />
                </div>
              </div>

              <div>
                <span className="fpe-rotulo">Recebimento</span>
                {p.destinoDeRecebimento ? (
                  <>
                    <div>
                      {p.destinoDeRecebimento.banco} · ag. {p.destinoDeRecebimento.agencia} · c/{' '}
                      {p.destinoDeRecebimento.conta}
                    </div>
                    <div className={p.destinoDeRecebimento.doProprioEmitente ? undefined : 'fpe-atencao'}>
                      {p.destinoDeRecebimento.doProprioEmitente
                        ? 'Conta do próprio emitente'
                        : `Recebe por ${p.destinoDeRecebimento.titular}`}
                    </div>
                  </>
                ) : (
                  <div className="fpe-ausente">sem conta cadastrada</div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {conjunto.ehConjunto && (
        <div className="fpe-total">
          <div className="fpe-total-titulo">
            Total consolidado da proposta
            <em>é a soma das parcelas acima — não some as duas coisas</em>
          </div>
          <div className="fpe-total-linhas">
            <div><span>Bruto</span><Valor v={totais.bruto} /></div>
            <div><span>Retenções</span><Valor v={totais.retencao} negativo /></div>
            <div><span>Taxa</span><Valor v={totais.taxa} negativo /></div>
            <div className="fpe-liquido"><span>Líquido</span><Valor v={totais.liquido} /></div>
          </div>
          {conjunto.totalConsolidadoNaAprovacao != null &&
            totais.bruto != null &&
            Math.abs(conjunto.totalConsolidadoNaAprovacao - totais.bruto) > 0.01 && (
              <p className="fpe-alerta">
                O total aprovado foi {moeda(conjunto.totalConsolidadoNaAprovacao)} e a soma das
                parcelas aqui é {moeda(totais.bruto)}. Confira antes de pagar.
              </p>
            )}
        </div>
      )}
    </div>
  );
}
